import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import styles from './UIGameActionButton.module.scss';
import { UIGameFloatingText } from '../UIGameFloatingText/index.ts';
import { UIGameCount } from '../UIGameCount/UIGameCount.tsx';
import { Tooltip } from '@colonist/ui';
import { PlayerColor } from '@colonist/game-states';
import { AudioManager, AudioType } from '@colonist/audio';
import BgButtonHighlight from '@static/images/bg_button_highlight.svg';
import BgButton from '@static/images/bg_button.svg';
import BgTradeButton from '@static/images/bg_button_trade.svg';
import BgTradeButtonHighlight from '@static/images/bg_button_trade_highlight.svg';
import { BACKGROUND_TRADE_BUTTON_IMAGE, getCooldownOverlayClipPath } from './utils.ts';
import ExecuteButtonEmbargoOverlay from '@static/images/execute_button_embargo_overlay.svg';
import { AnimatedWaitIcon } from '../AnimatedWaitIcon/index.ts';
const getBackgroundImage = (highlight, buttonType, backgroundColor)=>{
    if (buttonType === 'trade') {
        if (backgroundColor != null) {
            return BACKGROUND_TRADE_BUTTON_IMAGE[backgroundColor];
        }
        return highlight ? BgTradeButtonHighlight : BgTradeButton;
    }
    return highlight ? BgButtonHighlight : BgButton;
};
export const UIGameActionButton = ({ iconPath, disabled = false, shakingAnimation, helperText, count, countColor = PlayerColor.Red, highlight = false, tooltipTitle, tooltipContent, centerTextTooltip = false, tooltipCustomMenuComponent, tooltipPosition = 'top', tooltipMenuClassName, scalePulseAnimation, onClick, onClickActionIfDisabled, soundOnClick = AudioType.Click, className, buttonType = 'normal', backgroundColor, isEmbargoed, isCreatingCounterOffer, showCooldownMask, cooldownTimeMs, tooltipDisabled, iconClassName, customIconComponent, id })=>{
    const [isShaking, setIsShaking] = useState(false);
    const [cooldownPercentage, setCooldownPercentage] = useState(0);
    const shakeTimeoutRef = useRef(null);
    const cooldownIntervalRef = useRef(null);
    const cooldownStartTimeRef = useRef(null);
    const clearCooldownInterval = ()=>{
        if (cooldownIntervalRef.current) {
            clearInterval(cooldownIntervalRef.current);
            cooldownIntervalRef.current = null;
        }
    };
    const handleClick = ()=>{
        AudioManager.play(soundOnClick);
        if (shakingAnimation) {
            if (!isShaking) {
                setIsShaking(true);
            }
            if (shakeTimeoutRef.current) {
                clearTimeout(shakeTimeoutRef.current);
            }
            shakeTimeoutRef.current = setTimeout(()=>{
                setIsShaking(false);
                shakeTimeoutRef.current = null;
            }, 500);
        }
        if (disabled) {
            onClickActionIfDisabled === null || onClickActionIfDisabled === void 0 ? void 0 : onClickActionIfDisabled();
            return;
        }
        onClick === null || onClick === void 0 ? void 0 : onClick();
        setCooldownPercentage(100);
        clearCooldownInterval();
    };
    useEffect(()=>{
        if (shakeTimeoutRef.current) {
            clearTimeout(shakeTimeoutRef.current);
        }
    }, []);
    useEffect(()=>{
        const hasCooldownTime = cooldownTimeMs != null && cooldownTimeMs > 0;
        if (hasCooldownTime && showCooldownMask) {
            cooldownStartTimeRef.current = Date.now();
            setCooldownPercentage(0);
            const FRAME_RATE_30FPS_MS = 32;
            cooldownIntervalRef.current = setInterval(()=>{
                const currentTime = Date.now();
                var _cooldownStartTimeRef_current;
                const elapsed = currentTime - (((_cooldownStartTimeRef_current = cooldownStartTimeRef.current) !== null && _cooldownStartTimeRef_current !== void 0 ? _cooldownStartTimeRef_current : 0) || 0);
                const percentage = Math.min(elapsed / cooldownTimeMs * 100, 100);
                setCooldownPercentage(percentage);
                if (percentage >= 100) {
                    clearCooldownInterval();
                    handleClick();
                }
            }, FRAME_RATE_30FPS_MS);
        }
        return ()=>{
            clearCooldownInterval();
        };
    }, [
        cooldownTimeMs,
        showCooldownMask
    ]);
    const hasCount = count != null;
    const hasTooltip = tooltipContent != null && tooltipContent !== '' || tooltipTitle != null && tooltipTitle !== '' || tooltipCustomMenuComponent != null;
    const hasHelperText = helperText != null;
    const isTradeButton = buttonType === 'trade';
    const hasCustomIconComponent = customIconComponent != null && customIconComponent !== '';
    const button = /*#__PURE__*/ React.createElement("div", {
        className: clsx(styles.root, className),
        onClick: handleClick,
        id: id
    }, /*#__PURE__*/ React.createElement("div", {
        className: clsx(styles.contentWrapper, {
            [styles.shaking]: isShaking,
            [styles.scalePulse]: scalePulseAnimation
        })
    }, showCooldownMask && /*#__PURE__*/ React.createElement("img", {
        className: styles.cooldownMask,
        style: {
            clipPath: getCooldownOverlayClipPath(cooldownPercentage)
        },
        draggable: false,
        src: getBackgroundImage(true, buttonType, backgroundColor)
    }), isEmbargoed && /*#__PURE__*/ React.createElement("img", {
        className: styles.embargoOverlay,
        draggable: false,
        src: ExecuteButtonEmbargoOverlay
    }), isCreatingCounterOffer && /*#__PURE__*/ React.createElement(AnimatedWaitIcon, {
        className: styles.waitIcon,
        animationType: "bounce"
    }), /*#__PURE__*/ React.createElement("img", {
        className: clsx(styles.backgroundImage, {
            [styles.backgroundDisabled]: isTradeButton && disabled
        }),
        draggable: false,
        src: getBackgroundImage(highlight, buttonType, backgroundColor)
    }), /*#__PURE__*/ React.createElement("div", {
        className: clsx({
            [styles.foregroundDisabled]: disabled
        })
    }, hasCustomIconComponent ? /*#__PURE__*/ React.createElement("div", {
        className: clsx(styles.iconWrapper, iconClassName)
    }, customIconComponent) : /*#__PURE__*/ React.createElement("img", {
        className: clsx(styles.iconWrapper, iconClassName),
        draggable: false,
        src: iconPath
    }), hasCount && /*#__PURE__*/ React.createElement(UIGameCount, {
        className: clsx(styles.count),
        color: countColor,
        count: count
    }))));
    if (hasTooltip && hasHelperText) {
        return /*#__PURE__*/ React.createElement(Tooltip, {
            centerText: centerTextTooltip,
            content: tooltipContent,
            customMenuComponent: tooltipCustomMenuComponent,
            menuClassName: tooltipMenuClassName,
            position: tooltipPosition,
            disabled: tooltipDisabled,
            title: tooltipTitle
        }, /*#__PURE__*/ React.createElement(UIGameFloatingText, {
            direction: "up",
            text: helperText
        }, button));
    }
    if (hasHelperText) {
        return /*#__PURE__*/ React.createElement(UIGameFloatingText, {
            direction: "up",
            text: helperText
        }, button);
    }
    if (hasTooltip) {
        return /*#__PURE__*/ React.createElement(Tooltip, {
            centerText: centerTextTooltip,
            content: tooltipContent,
            customMenuComponent: tooltipCustomMenuComponent,
            menuClassName: tooltipMenuClassName,
            position: tooltipPosition,
            disabled: tooltipDisabled,
            title: tooltipTitle
        }, button);
    }
    return button;
};
